import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Sentry } from './lib/sentry';
import { ShowsList } from './components/ShowsList';
import { LandingPage } from './pages/LandingPage';
import { SellerHub } from './components/SellerHub';
import { CreateShow } from './components/CreateShow';
import { SellerDashboard } from './components/SellerDashboard';
import { BuyerView } from './components/BuyerView';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { OrdersPage } from './pages/OrdersPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { SellerSalesPage } from './pages/SellerSalesPage';
import { SellerSaleDetailPage } from './pages/SellerSaleDetailPage';
import { SellerOrdersPage } from './pages/SellerOrdersPage';
import { SellerOrderDetailPage } from './pages/SellerOrderDetailPage';
import { SellerInventoryPage } from './pages/SellerInventoryPage';
import { SellerShippingPage } from './pages/SellerShippingPage';
import { SellerPayoutsPage } from './pages/SellerPayoutsPage';
import { SellerShipmentsPage } from './pages/SellerShipmentsPage';
import { SellerShipmentDetailPage } from './pages/SellerShipmentDetailPage';
import { SellerShowsPage } from './pages/SellerShowsPage';
import { SellerShowDetailPage } from './pages/SellerShowDetailPage';
import { SellerBannedUsersPage } from './pages/SellerBannedUsersPage';

import { InboxPage } from './pages/InboxPage';
import { AccountPage } from './pages/AccountPage';
import { EditProfilePage } from './pages/EditProfilePage';
import { PublicProfilePage } from './pages/PublicProfilePage';
import { ShippingAddressesPage } from './pages/ShippingAddressesPage';
import { PaymentMethodsPage } from './pages/PaymentMethodsPage';
import { BalancePage } from './pages/BalancePage';
import { HelpPage } from './pages/HelpPage';
import { StripeConnectReturnPage } from './pages/StripeConnectReturnPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { DisplayPage } from './pages/DisplayPage';
import { SecurityPage } from './pages/SecurityPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { SessionsPage } from './pages/SessionsPage';
import { ChangeEmailPage } from './pages/ChangeEmailPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { DataDeletionPage } from './pages/DataDeletionPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { AppInfoPage } from './pages/AppInfoPage';
import { AboutPage } from './pages/AboutPage';
import { SupportPage } from './pages/SupportPage';
import { VerificationModal } from './components/VerificationModal';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { StreamProvider } from './contexts/StreamContext';
import { Layout } from './components/Layout';
import { AuthPromptModal } from './components/AuthPromptModal';
import { colors } from './constants/colors';
import type { ReactNode } from 'react';
import { useState, useEffect, createContext, useContext } from 'react';

// Protected route wrapper - requires authentication
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [showAuth, setShowAuth] = useState(true);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: colors.gray[100], color: colors.gray[900] }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Navigate to="/" replace />
        <AuthPromptModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          onAuthenticated={() => setShowAuth(false)}
        />
      </>
    );
  }

  return <>{children}</>;
}

// Seller route wrapper - requires authentication and seller role
function SellerRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [showAuth, setShowAuth] = useState(true);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: colors.gray[100], color: colors.gray[900] }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Navigate to="/" replace />
        <AuthPromptModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          onAuthenticated={() => setShowAuth(false)}
        />
      </>
    );
  }

  if (!user?.isSeller) {
    return <Navigate to="/sell" replace />;
  }

  return <>{children}</>;
}

const VerificationCtx = createContext<{ showVerification: () => void }>({ showVerification: () => {} });
export function useVerification() { return useContext(VerificationCtx); }

function VerificationGate({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const needsVerification = isAuthenticated && user && (!user.phoneNumber || !user.phoneVerified || !user.emailVerified || user.needsProfileSetup);

  return (
    <VerificationCtx.Provider value={{ showVerification: () => setDismissed(false) }}>
      {children}
      {needsVerification && !dismissed && (
        <VerificationModal
          onComplete={async () => {
            await refreshUser();
          }}
          onDismiss={() => setDismissed(true)}
        />
      )}
    </VerificationCtx.Provider>
  );
}


// Logout route component - works even when verification modal is blocking
function LogoutRoute() {
  const { logout, isAuthenticated } = useAuth();
  useEffect(() => {
    if (isAuthenticated) {
      logout();
    }
  }, [logout, isAuthenticated]);
  return <Navigate to="/" replace />;
}

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

function AppRoutes() {
  return (
    <SentryRoutes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/shows" element={<ShowsList />} />
      <Route path="/login" element={<Navigate to="/shows" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/terms" element={<Layout><TermsOfServicePage /></Layout>} />
      <Route path="/privacy" element={<Layout><PrivacyPolicyPage /></Layout>} />
      <Route path="/data-deletion" element={<Layout><DataDeletionPage /></Layout>} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/support" element={<Layout><SupportPage /></Layout>} />

      {/* Public profile route */}
      <Route path="/user/:username" element={<PublicProfilePage />} />

      {/* Protected routes (requires auth) */}
      <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
      <Route path="/orders/:orderId" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
      <Route path="/account/edit" element={<ProtectedRoute><EditProfilePage /></ProtectedRoute>} />
      <Route path="/display" element={<ProtectedRoute><DisplayPage /></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute><SecurityPage /></ProtectedRoute>} />
      <Route path="/security/password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
      <Route path="/security/sessions" element={<ProtectedRoute><SessionsPage /></ProtectedRoute>} />
      <Route path="/security/email" element={<ProtectedRoute><ChangeEmailPage /></ProtectedRoute>} />
      <Route path="/addresses" element={<ProtectedRoute><ShippingAddressesPage /></ProtectedRoute>} />
      <Route path="/payment-methods" element={<ProtectedRoute><PaymentMethodsPage /></ProtectedRoute>} />
      <Route path="/balance" element={<ProtectedRoute><BalancePage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
      <Route path="/app-info" element={<ProtectedRoute><AppInfoPage /></ProtectedRoute>} />
      <Route path="/sell" element={<ProtectedRoute><SellerHub /></ProtectedRoute>} />
      <Route path="/watch/:showId" element={<BuyerView />} />

      {/* Seller routes (requires auth + seller role) */}
      <Route path="/sell/live" element={<SellerRoute><CreateShow /></SellerRoute>} />
      <Route path="/sell/live/:showId" element={<SellerRoute><SellerDashboard /></SellerRoute>} />
      <Route path="/sell/shows" element={<SellerRoute><SellerShowsPage /></SellerRoute>} />
      <Route path="/sell/shows/:showId" element={<SellerRoute><SellerShowDetailPage /></SellerRoute>} />
      <Route path="/sell/sales" element={<SellerRoute><SellerSalesPage /></SellerRoute>} />
      <Route path="/sell/sales/:saleId" element={<SellerRoute><SellerSaleDetailPage /></SellerRoute>} />
      <Route path="/sell/orders" element={<SellerRoute><SellerOrdersPage /></SellerRoute>} />
      <Route path="/sell/orders/:orderId" element={<SellerRoute><SellerOrderDetailPage /></SellerRoute>} />
      <Route path="/sell/shipments" element={<SellerRoute><SellerShipmentsPage /></SellerRoute>} />
      <Route path="/sell/shipments/:shipmentId" element={<SellerRoute><SellerShipmentDetailPage /></SellerRoute>} />
      <Route path="/sell/inventory" element={<SellerRoute><SellerInventoryPage /></SellerRoute>} />
      <Route path="/sell/shipping" element={<SellerRoute><SellerShippingPage /></SellerRoute>} />
      <Route path="/sell/payouts" element={<SellerRoute><SellerPayoutsPage /></SellerRoute>} />
      <Route path="/sell/banned-users" element={<SellerRoute><SellerBannedUsersPage /></SellerRoute>} />

      <Route path="/sell/settings" element={<Navigate to="/sell/shipping" replace />} />
      <Route path="/seller/stripe/return" element={<SellerRoute><StripeConnectReturnPage /></SellerRoute>} />
      <Route path="/seller/stripe/refresh" element={<SellerRoute><StripeConnectReturnPage /></SellerRoute>} />

      {/* 404 catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </SentryRoutes>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.gray[100] }}>
        <div style={{ backgroundColor: colors.white, borderRadius: '16px', padding: '40px', maxWidth: '500px', textAlign: 'center', border: `1px solid ${colors.gray[300]}` }}>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: colors.ink.black, marginBottom: '12px' }}>Something went wrong</h1>
          <p style={{ color: colors.gray[600], fontSize: '14px', marginBottom: '20px' }}>An unexpected error occurred. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{ backgroundColor: colors.brand.blue, color: colors.white, border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
          >
            Refresh Page
          </button>
        </div>
      </div>
    }>
      <BrowserRouter>
        <AuthProvider>
          <StreamProvider>
            <Routes>
              <Route path="/logout" element={<LogoutRoute />} />
              <Route path="*" element={
                <VerificationGate>
                  <AppRoutes />
                </VerificationGate>
              } />
            </Routes>
          </StreamProvider>
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

export default App;
